/**
 * Orchestrates Phase 8 offline sign-in end to end against the actual local
 * database: PIN verification + lockout, capability-grant verification, and
 * starting a bounded `local_session` row. `grant.ts`/`lockout.ts`
 * (this package) and `hashOfflinePin`/`verifyOfflinePin` (`@kortex/shared`)
 * are all deliberately pure/network-free (see their own doc comments) and
 * none of them touch `local_config`/`users` - this module is where they
 * meet the actual driver.
 *
 * `apps/web`'s OfflineSetup.tsx/OfflineUnlock.tsx call into this class
 * (`LocalDatabase.auth`); nothing in `pages/` talks to `local_config`/
 * `users` columns or the lockout/grant primitives directly.
 */
import { verifyOfflinePin } from '@kortex/shared';
import { LocalDataError } from '../types.js';
import { ConfigRepository } from '../repos/config.js';
import { SessionRepository } from '../repos/sessions.js';
import { isGrantExpired, OfflineGrantError, verifyOfflineGrant } from './grant.js';
import { deviceContactState } from './contact.js';
import { clearLockout, INITIAL_LOCKOUT_STATE, isLocked, recordFailedAttempt, } from './lockout.js';
const GRANT_PUBLIC_KEY_CONFIG_KEY = 'offline.grantPublicKey';
const lockoutConfigKey = (userId) => `offline.lockout.${userId}`;
const permissionsConfigKey = (userId) => `offline.permissions.${userId}`;
/**
 * A cashier's local unlock lasts at most this long, further capped by
 * whatever's left on the grant itself (PHASE-8-ARCHITECTURE.md §3.4: a
 * session's own expiry is set at unlock time, independent of the grant, but
 * it can never exceed the grant - an expired grant cannot mint a new
 * session no matter how long a shift-length session would otherwise run).
 * A fixed 12h "shift" is a deliberate, documented scope choice, not a value
 * carried in the grant or property settings - a future phase could make it
 * configurable if a 12h shift turns out to be the wrong assumption.
 */
export const LOCAL_SESSION_HOURS = 12;
/**
 * Always thrown by `unlockOffline` in preference to a bare `LocalDataError`
 * or `OfflineGrantError`, so the UI can switch on `.reason` for exactly the
 * five cases PHASE-8-ARCHITECTURE.md §3 anticipates instead of pattern-
 * matching error message text.
 */
export class OfflineUnlockError extends LocalDataError {
    reason;
    /** Only set for LOCKED_OUT - when the lock lifts, for the UI to count down to. */
    lockedUntil;
    constructor(reason, message, lockedUntil) {
        super(reason, message);
        this.name = 'OfflineUnlockError';
        this.reason = reason;
        this.lockedUntil = lockedUntil;
    }
}
export class OfflineAuthRepository {
    driver;
    config;
    sessions;
    constructor(driver) {
        this.driver = driver;
        this.config = new ConfigRepository(driver);
        this.sessions = new SessionRepository(driver);
    }
    async pinGrantPublicKey(publicKeySpki) {
        await this.config.set(GRANT_PUBLIC_KEY_CONFIG_KEY, publicKeySpki);
    }
    async getPinnedGrantPublicKey() {
        return this.config.get(GRANT_PUBLIC_KEY_CONFIG_KEY);
    }
    async setOfflineVerifier(userId, verifier) {
        await this.driver.execute({
            sql: 'UPDATE users SET offline_verifier = ? WHERE id = ?',
            params: [verifier, userId],
        });
    }
    async setGrant(userId, token, notAfterEpochSeconds) {
        await this.driver.execute({
            sql: 'UPDATE users SET grant_json = ?, grant_expires_at = ? WHERE id = ?',
            params: [token, new Date(notAfterEpochSeconds * 1000).toISOString(), userId],
        });
    }
    async getCredentials(userId) {
        const rows = await this.driver.query({
            sql: 'SELECT offline_verifier, grant_json, grant_expires_at, role FROM users WHERE id = ?',
            params: [userId],
        });
        const row = rows[0];
        return {
            offlineVerifier: row?.offline_verifier == null ? null : String(row.offline_verifier),
            grantToken: row?.grant_json == null ? null : String(row.grant_json),
            grantExpiresAt: row?.grant_expires_at == null ? null : String(row.grant_expires_at),
            role: row?.role == null ? null : String(row.role),
        };
    }
    async cachePermissions(userId, permissions) {
        await this.config.setJson(permissionsConfigKey(userId), permissions);
    }
    async getCachedPermissions(userId) {
        return (await this.config.getJson(permissionsConfigKey(userId))) ?? [];
    }
    /**
     * The one local user who has completed offline enrolment on this device,
     * with the property fields `OfflineUnlock.tsx` needs to build a `Session`
     * without a network call - the local database is pinned to exactly one
     * property (`local_device`'s whole design), so a plain `CROSS JOIN`
     * against the singleton `property` row is safe. `null` when nobody has
     * enrolled yet (a fresh or reset browser) - the caller falls back to the
     * ordinary online sign-in form in that case.
     */
    async enrolledUser() {
        const rows = await this.driver.query({
            sql: `SELECT u.id AS user_id, u.name AS user_name, u.email AS user_email,
                   p.id AS property_id, p.name AS property_name, p.gstin, p.state_code
            FROM users u CROSS JOIN property p
            WHERE u.offline_verifier IS NOT NULL
            LIMIT 1`,
        });
        const row = rows[0];
        if (!row)
            return null;
        return {
            userId: String(row.user_id),
            name: String(row.user_name),
            email: String(row.user_email),
            propertyId: String(row.property_id),
            propertyName: String(row.property_name),
            gstin: String(row.gstin),
            stateCode: String(row.state_code),
        };
    }
    /** Whether `userId` has completed local-mode enrolment: a PIN, a grant,
     *  and a pinned public key to verify it with - all three, or none of
     *  this can actually authenticate the user offline. */
    async isEnrolled(userId) {
        const [credentials, publicKey] = await Promise.all([
            this.getCredentials(userId),
            this.getPinnedGrantPublicKey(),
        ]);
        return Boolean(credentials.offlineVerifier && credentials.grantToken && publicKey);
    }
    async getLockoutState(userId) {
        return (await this.config.getJson(lockoutConfigKey(userId))) ?? INITIAL_LOCKOUT_STATE;
    }
    async recordFailedPinAttempt(userId, now = new Date()) {
        const next = recordFailedAttempt(await this.getLockoutState(userId), now);
        await this.config.setJson(lockoutConfigKey(userId), next);
        return next;
    }
    async clearPinLockout(userId) {
        await this.config.setJson(lockoutConfigKey(userId), clearLockout());
    }
    /**
     * One call for `OfflineSetup.tsx`, made after it has already fetched a
     * verifier from `POST /api/auth/offline-pin`, a grant from
     * `POST /api/auth/offline-grant` and a public key from
     * `GET /api/auth/offline-grant/public-key`: stores all three locally so
     * `unlockOffline` has what it needs. Pins the grant public key, stores
     * the (already-hashed) verifier, and stores the grant.
     */
    async enableOfflineSignIn(userId, input) {
        await this.pinGrantPublicKey(input.grantPublicKeySpki);
        await this.setOfflineVerifier(userId, input.verifier);
        await this.setGrant(userId, input.grantToken, input.grantNotAfterEpochSeconds);
        await this.cachePermissions(userId, input.permissions);
        await this.clearPinLockout(userId);
    }
    /**
     * The whole offline sign-in flow: lockout check -> PIN verify (recording
     * a failed attempt on mismatch) -> grant verify against the pinned
     * public key -> start a bounded `local_session` row.
     */
    async unlockOffline(userId, pin, now = new Date()) {
        const [credentials, publicKey, lockout] = await Promise.all([
            this.getCredentials(userId),
            this.getPinnedGrantPublicKey(),
            this.getLockoutState(userId),
        ]);
        if (!credentials.offlineVerifier || !credentials.grantToken || !publicKey) {
            throw new OfflineUnlockError('NOT_ENROLLED', 'This device has not been set up for offline sign-in yet');
        }
        // Before the PIN: a locked till is not a place to be guessing PINs.
        const contact = await deviceContactState(this.config, now);
        if (contact.locked) {
            throw new OfflineUnlockError('DEVICE_LOCKED', contact.reason === 'STALE'
                ? `This device has not reached the server for more than ${contact.lockDays} days. Connect it to the internet to unlock it.`
                : "This device's clock is earlier than its last contact with the server. Correct the date and time, or connect it to the internet.");
        }
        if (isLocked(lockout, now)) {
            throw new OfflineUnlockError('LOCKED_OUT', 'Too many wrong PINs. Try again later.', lockout.lockedUntil ?? undefined);
        }
        const pinOk = await verifyOfflinePin(pin, credentials.offlineVerifier);
        if (!pinOk) {
            await this.recordFailedPinAttempt(userId, now);
            throw new OfflineUnlockError('WRONG_PIN', 'Incorrect PIN');
        }
        await this.clearPinLockout(userId);
        let grant;
        try {
            grant = await verifyOfflineGrant(credentials.grantToken, publicKey, now);
        }
        catch (error) {
            if (error instanceof OfflineGrantError && error.code === 'EXPIRED') {
                throw new OfflineUnlockError('GRANT_EXPIRED', 'Offline sign-in has expired. Connect to the internet once to renew it.');
            }
            throw new OfflineUnlockError('GRANT_INVALID', 'Offline sign-in credentials are invalid. Connect to the internet once to fix this.');
        }
        if (isGrantExpired(grant.notAfter, now)) {
            throw new OfflineUnlockError('GRANT_EXPIRED', 'Offline sign-in has expired. Connect to the internet once to renew it.');
        }
        const sessionExpiry = new Date(Math.min(now.getTime() + LOCAL_SESSION_HOURS * 60 * 60 * 1000, grant.notAfter * 1000));
        const [session, permissions] = await Promise.all([
            this.sessions.start(userId, sessionExpiry.toISOString()),
            this.getCachedPermissions(userId),
        ]);
        return { session, role: credentials.role ?? '', permissions };
    }
}
//# sourceMappingURL=offlineAuth.js.map